import { createFileRoute } from "@tanstack/react-router";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  ShoppingCart,
  Users,
  TrendingUp,
  Scissors,
  Smartphone,
  Copy,
  ChevronUp,
  ChevronDown,
  Minus,
  Repeat,
  BadgePercent,
  AlertTriangle,
  UserMinus,
} from "lucide-react";
import { trpc } from "@/trpc/trpc";
import { toast } from "sonner";

export const Route = createFileRoute("/_authenticated/")({
  component: DashboardPage,
});

function formatPrice(cents: number) {
  return `${(cents / 100).toFixed(2)} zł`;
}

function StatCard({
  title,
  value,
  description,
  icon: Icon,
}: {
  title: string;
  value: string;
  description?: string;
  icon: React.ElementType;
}) {
  return (
    <Card className="p-6">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-muted-foreground">{title}</p>
          <p className="mt-1 text-2xl font-bold">{value}</p>
          {description && (
            <p className="mt-1 text-xs text-muted-foreground">{description}</p>
          )}
        </div>
        <div className="flex size-10 items-center justify-center rounded-md bg-primary/10">
          <Icon className="size-5 text-primary" />
        </div>
      </div>
    </Card>
  );
}

const statusLabels: Record<string, string> = {
  pending_payment: "Oczekuje",
  paid: "Opłacone",
  confirmed: "Potwierdzone",
  completed: "Zrealizowane",
  cancelled: "Anulowane",
  refunded: "Zwrócone",
};

function DashboardPage() {
  const { data: me, isLoading } = trpc.meRouter.getMe.useQuery();

  if (isLoading || !me) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  if (me.salonRole === "operator") {
    return <OperatorDashboard />;
  }

  return <OwnerDashboard />;
}

function OperatorDashboard() {
  const { data: stats, isLoading } = trpc.salon.getMyOperatorStats.useQuery();

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  function copyCode() {
    if (stats?.code) {
      navigator.clipboard.writeText(stats.code);
      toast.success("Kod skopiowany");
    }
  }

  const trend = stats
    ? stats.monthlyInstalls - stats.previousMonthInstalls
    : 0;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard operatora</h1>
        <p className="text-sm text-muted-foreground">
          Twoje statystyki instalacji
        </p>
      </div>

      {stats ? (
        <>
          <Card className="p-6">
            <p className="text-sm text-muted-foreground">Twój kod operatora</p>
            <div className="mt-2 flex items-center gap-3">
              <span className="rounded-lg bg-muted px-4 py-2 font-mono text-2xl font-bold tracking-widest">
                {stats.code}
              </span>
              <button
                onClick={copyCode}
                className="rounded-md p-2 hover:bg-muted"
              >
                <Copy className="size-5 text-muted-foreground" />
              </button>
            </div>
            <p className="mt-2 text-xs text-muted-foreground">
              Podaj ten kod klientom przy instalacji aplikacji
            </p>
          </Card>

          <div className="grid gap-4 sm:grid-cols-3">
            <StatCard
              title="Instalacje łącznie"
              value={String(stats.totalInstalls)}
              icon={Smartphone}
            />
            <StatCard
              title="Poprzedni miesiąc"
              value={String(stats.previousMonthInstalls)}
              icon={Smartphone}
            />
            <Card className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Ten miesiąc</p>
                  <p className="mt-1 text-2xl font-bold">{stats.monthlyInstalls}</p>
                  {trend !== 0 && (
                    <p className={`mt-1 flex items-center gap-1 text-xs ${trend > 0 ? "text-green-600" : "text-red-500"}`}>
                      {trend > 0 ? <ChevronUp className="size-3" /> : <ChevronDown className="size-3" />}
                      {trend > 0 ? "+" : ""}{trend} vs poprzedni
                    </p>
                  )}
                  {trend === 0 && stats.previousMonthInstalls > 0 && (
                    <p className="mt-1 flex items-center gap-1 text-xs text-muted-foreground">
                      <Minus className="size-3" /> bez zmian
                    </p>
                  )}
                </div>
                <div className="flex size-10 items-center justify-center rounded-md bg-primary/10">
                  <Smartphone className="size-5 text-primary" />
                </div>
              </div>
            </Card>
          </div>
        </>
      ) : (
        <Card className="p-6">
          <p className="text-muted-foreground">
            Nie masz jeszcze przypisanego kodu operatora. Skontaktuj się z właścicielem salonu.
          </p>
        </Card>
      )}
    </div>
  );
}

function OwnerDashboard() {
  const { data, isLoading } = trpc.dashboard.getStats.useQuery();
  const { data: salon, isLoading: salonLoading } =
    trpc.salon.getOwnerSalon.useQuery();
  // Display only — some owners don't want the totals on screen. Until the
  // setting is known the totals stay hidden, so they never flash for an owner
  // who turned them off.
  const hideRevenue = salon?.hideRevenueStats ?? true;

  if (isLoading || salonLoading) {
    return <p className="text-sm text-muted-foreground">Ładowanie...</p>;
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Dashboard</h1>
        <p className="text-sm text-muted-foreground">
          Przegląd Twojego salonu
        </p>
      </div>

      <div
        className={`grid gap-4 sm:grid-cols-2 ${hideRevenue ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}
      >
        {!hideRevenue && (
          <StatCard
            title="Przychody (miesiąc)"
            value={formatPrice(data?.monthlyRevenue ?? 0)}
            icon={TrendingUp}
          />
        )}
        <StatCard
          title="Zakupy (miesiąc)"
          value={String(data?.monthlyPurchases ?? 0)}
          icon={ShoppingCart}
        />
        <StatCard
          title="Klienci"
          value={String(data?.activeClients ?? 0)}
          icon={Users}
        />
        <StatCard
          title="Aktywne zabiegi"
          value={String(data?.activeTreatments ?? 0)}
          icon={Scissors}
        />
      </div>

      {data?.subscriptions && (
        <div
          className={`grid gap-4 sm:grid-cols-2 ${hideRevenue ? "lg:grid-cols-3" : "lg:grid-cols-4"}`}
        >
          {!hideRevenue && (
            <StatCard
              title="MRR z abonamentów"
              value={formatPrice(data.subscriptions.mrr)}
              description={
                data.subscriptions.mrrAtRisk > 0
                  ? `+ ${formatPrice(data.subscriptions.mrrAtRisk)} zagrożone (zaległe)`
                  : "miesięczny przychód z aktywnych abonamentów"
              }
              icon={Repeat}
            />
          )}
          <StatCard
            title="Aktywne abonamenty"
            value={String(data.subscriptions.active)}
            description={
              data.subscriptions.paused > 0
                ? `${data.subscriptions.paused} wstrzymanych`
                : undefined
            }
            icon={BadgePercent}
          />
          <StatCard
            title="Zaległe płatności"
            value={String(data.subscriptions.pastDue)}
            description="abonamenty w trakcie ponownych prób obciążenia"
            icon={AlertTriangle}
          />
          <StatCard
            title="Zgłoszone rezygnacje"
            value={String(data.subscriptions.pendingCancellations)}
            description="aktywne, ale kończą się z końcem okresu"
            icon={UserMinus}
          />
        </div>
      )}

      <Card className="p-6">
        <h2 className="mb-4 text-lg font-semibold">Ostatnie zakupy</h2>
        {data?.recentPurchases.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak zakupów</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-muted-foreground">
                  <th className="pb-2 pr-4 font-medium">Kod</th>
                  <th className="pb-2 pr-4 font-medium">Klient</th>
                  <th className="pb-2 pr-4 font-medium">Zabieg</th>
                  <th className="pb-2 pr-4 font-medium">Kwota</th>
                  <th className="pb-2 pr-4 font-medium">Status</th>
                  <th className="pb-2 font-medium">Data</th>
                </tr>
              </thead>
              <tbody>
                {data?.recentPurchases.map((p) => (
                  <tr key={p.id} className="border-b last:border-0">
                    <td className="py-2 pr-4">
                      <span className="font-mono text-xs">{p.code}</span>
                    </td>
                    <td className="py-2 pr-4">{p.customerName}</td>
                    <td className="py-2 pr-4">{p.treatmentName}</td>
                    <td className="py-2 pr-4">{formatPrice(p.amount)}</td>
                    <td className="py-2 pr-4">
                      <Badge variant="secondary">
                        {statusLabels[p.status] ?? p.status}
                      </Badge>
                    </td>
                    <td className="py-2 text-muted-foreground">
                      {new Date(p.createdAt).toLocaleDateString("pl-PL")}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <OperatorCodesCard />
    </div>
  );
}

function OperatorCodesCard() {
  const { data: operators, isLoading } = trpc.salon.getOperatorStats.useQuery();

  if (isLoading || !operators || operators.length === 0) return null;

  return (
    <Card className="p-6">
      <div className="mb-4 flex items-center gap-2">
        <Smartphone className="size-5 text-primary" />
        <h2 className="text-lg font-semibold">Kody operatorów</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="pb-2 pr-4 font-medium">Operator</th>
              <th className="pb-2 pr-4 font-medium">Kod</th>
              <th className="pb-2 font-medium">Instalacje</th>
            </tr>
          </thead>
          <tbody>
            {operators.map((op) => (
              <tr key={op.code} className="border-b last:border-0">
                <td className="py-2 pr-4">{op.operatorName}</td>
                <td className="py-2 pr-4">
                  <span className="rounded bg-muted px-2 py-1 font-mono text-xs">
                    {op.code}
                  </span>
                </td>
                <td className="py-2 font-semibold">{op.totalInstalls}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
